'use client';

import type { TextInputProps } from '../text-input';

import { cn } from '@polyseme/utils/cn/cn-utils';
import {
  dayjsDate,
  formatBirthDate,
  formatDate,
  isBirthDateValid,
  unformatBirthDate,
} from '@polyseme/utils/date/date-utils';
import { CalendarIcon } from 'lucide-react';
import mergeRefs from 'merge-refs';
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '../../../ui/button';
import { Calendar } from '../../../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../../ui/popover';
import { TextInput } from '../text-input';

type DatePickerProps = Omit<TextInputProps, 'onChange' | 'value'> & {
  onChange: (value: string) => void;
  /** YYYY-MM-DD format */
  value?: string;
};

const MASK_OPTIONS = { mask: '__/__/____', replacement: { _: /\d/ } } as const;

const PASTE_FORMATS = [
  'YYYY-MM-DD',
  'DD/MM/YYYY',
  'DD-MM-YYYY',
  'DD.MM.YYYY',
] as const;

/**
 * Try to parse a raw string (pasted or autofilled) into DD/MM/YYYY
 * using dayjs strict parsing with multiple format attempts.
 */
const tryParseRawDate = (raw: string): string | null => {
  const trimmed = raw.trim();
  for (const fmt of PASTE_FORMATS) {
    const d = dayjsDate(trimmed, fmt as 'DD/MM/YYYY', true);
    if (d.isValid()) return formatDate(d, 'DD/MM/YYYY');
  }
  return null;
};

const toDateObject = (isoDate: string): Date | undefined => {
  if (!isoDate) return undefined;
  const d = dayjsDate(isoDate, 'YYYY-MM-DD', true);
  return d.isValid() ? d.toDate() : undefined;
};

/**
 * Replace the input content programmatically by selecting all then inserting
 * digits via execCommand so the mask library processes them through its
 * normal beforeinput flow.
 */
const replaceInputValue = (input: HTMLInputElement, ddmmyyyy: string) => {
  const digits = ddmmyyyy.replace(/\D/g, '');
  input.focus();
  input.setSelectionRange(0, input.value.length);
  document.execCommand('insertText', false, digits);
};

export const DatePicker = forwardRef<HTMLInputElement, DatePickerProps>(
  ({ className, onChange, value, ...props }, ref) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const lastEmittedValue = useRef(value);
    const [open, setOpen] = useState(false);

    // Sync input when value changes externally (e.g. RHF reset/setValue)
    useEffect(() => {
      if (value === lastEmittedValue.current) return;
      lastEmittedValue.current = value;
      if (inputRef.current) {
        const display = value ? unformatBirthDate(value) : '';
        if (display) {
          replaceInputValue(inputRef.current, display);
        } else {
          inputRef.current.value = '';
        }
      }
    }, [value]);

    const handleInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        if (isBirthDateValid(raw)) {
          const formatted = formatBirthDate(raw);
          lastEmittedValue.current = formatted;
          onChange(formatted);
        } else {
          lastEmittedValue.current = raw;
          onChange(raw);
        }
      },
      [onChange]
    );

    const handlePaste = useCallback(
      (e: React.ClipboardEvent<HTMLInputElement>) => {
        const text = e.clipboardData.getData('text');
        const parsed = tryParseRawDate(text);
        if (parsed && inputRef.current) {
          e.preventDefault();
          replaceInputValue(inputRef.current, parsed);
        }
      },
      []
    );

    const handleCalendarSelect = useCallback((date: Date | undefined) => {
      if (!date || !inputRef.current) return;
      const iso = formatDate(date, 'YYYY-MM-DD');
      replaceInputValue(inputRef.current, unformatBirthDate(iso));
      setOpen(false);
    }, []);

    const selectedDate = toDateObject(value ?? '');

    return (
      <div className={cn('flex items-center gap-2', className)}>
        <TextInput
          ref={mergeRefs(ref, inputRef)}
          data-testid="date-picker"
          {...props}
          defaultValue={value ? unformatBirthDate(value) : undefined}
          inputMode="numeric"
          mask={MASK_OPTIONS}
          placeholder="JJ/MM/AAAA"
          onChange={handleInputChange}
          onPaste={handlePaste}
        />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              isIcon
              className="size-9 shrink-0 lg:size-10"
              type="button"
              variant="outline"
            >
              <CalendarIcon className="size-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-auto p-0 lg:w-auto">
            <Calendar
              captionLayout="dropdown"
              defaultMonth={selectedDate}
              endMonth={new Date()}
              mode="single"
              selected={selectedDate}
              startMonth={new Date(1900, 0)}
              onSelect={handleCalendarSelect}
            />
          </PopoverContent>
        </Popover>
      </div>
    );
  }
);

DatePicker.displayName = 'DatePicker';
